import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import { lazy, Suspense, useEffect, useState } from "react";
import type { ReactNode } from "react";

import { applySetupCommand, newIdempotencyKey, setupErrorMessage } from "@/libs/tournamentSetupApi";
import type { SetupCommand, SetupDay, TournamentSetupView } from "@/libs/tournamentSetupApi";

import { CourtsPicker, useFacilityCourts } from "../setup/courts-picker";
import { DaysEditor } from "../setup/days-editor";
import { RegistrationWindowFields } from "../setup/registration-window-fields";
import type { RegistrationWindow } from "../setup/registration-window-fields";
import { CloseIcon, DenseButton, DenseInput, FieldLabel, Note, SetupSurface, tone } from "../setup/ui";

const LegalTab = lazy(() => import("../legal-tab").then((m) => ({ default: m.LegalTab })));
const StaffTab = lazy(() => import("../staff-tab").then((m) => ({ default: m.StaffTab })));
const CoachesTab = lazy(() => import("../coaches-tab").then((m) => ({ default: m.CoachesTab })));
const SponsorsTab = lazy(() => import("../sponsors-tab").then((m) => ({ default: m.SponsorsTab })));

export type SheetTarget = "basics" | "registration" | "waiver" | "officials" | "promo";

const TITLES: Record<SheetTarget, string> = {
  basics: "Name, days and courts",
  registration: "Registration window",
  waiver: "Waiver and tournament rules",
  officials: "Referee and officials",
  promo: "Poster, sponsors and prizes",
};

function useCommand(view: TournamentSetupView, onSaved: (next: TournamentSetupView) => void) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (commands: Omit<SetupCommand, "idempotency_key" | "expected_revision">[]) => {
    setPending(true);
    setError(null);
    try {
      let revision = view.revision;
      let latest = view;
      for (const command of commands) {
        latest = await applySetupCommand(view.tournament_id, {
          ...command,
          idempotency_key: newIdempotencyKey(),
          expected_revision: revision,
        } as SetupCommand);
        revision = latest.revision;
      }
      onSaved(latest);
      return true;
    } catch (cause) {
      setError(setupErrorMessage(cause));
      return false;
    } finally {
      setPending(false);
    }
  };
  return { pending, error, run };
}

function BasicsForm({ view, facilityId, onSaved, onDone }: { view: TournamentSetupView; facilityId: string; onSaved: (v: TournamentSetupView) => void; onDone: () => void }) {
  const [name, setName] = useState(view.name);
  const [days, setDays] = useState<SetupDay[]>(view.days);
  const [courtIds, setCourtIds] = useState(view.courts.map((court) => court.facility_court_id));
  const courts = useFacilityCourts(facilityId);
  const { pending, error, run } = useCommand(view, onSaved);
  const save = async () => {
    const commands: Omit<SetupCommand, "idempotency_key" | "expected_revision">[] = [];
    if (name.trim() !== view.name) commands.push({ command: "update_basics", name: name.trim() } as never);
    if (JSON.stringify(days) !== JSON.stringify(view.days)) commands.push({ command: "set_days", days } as never);
    if (JSON.stringify(courtIds) !== JSON.stringify(view.courts.map((c) => c.facility_court_id))) {
      commands.push({ command: "set_courts", facility_court_ids: courtIds } as never);
    }
    if (!commands.length || (await run(commands))) onDone();
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <FieldLabel htmlFor="sheet-name">Name</FieldLabel>
        <DenseInput id="sheet-name" onChange={(event) => setName(event.target.value)} value={name} />
      </Box>
      <DaysEditor
        days={days}
        onChange={setDays}
        trailing={<CourtsPicker courts={courts ?? []} facilityName={view.facility_name} onChange={setCourtIds} value={courtIds} />}
      />
      {view.hold_line ? <Note tone="neutral">{view.hold_line}</Note> : null}
      {error ? <Note role="alert" tone="red">{error}</Note> : null}
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <DenseButton onClick={onDone}>Cancel</DenseButton>
        <DenseButton disabled={pending} onClick={save} variant="primary">
          Save
        </DenseButton>
      </Box>
    </Box>
  );
}

function RegistrationForm({ view, onSaved, onDone }: { view: TournamentSetupView; onSaved: (v: TournamentSetupView) => void; onDone: () => void }) {
  const [value, setValue] = useState<RegistrationWindow>({
    opens: view.registration_opens,
    openAt: view.registration_open_at ?? null,
    closeAt: view.registration_close_at ?? null,
  });
  const { pending, error, run } = useCommand(view, onSaved);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <RegistrationWindowFields hint={view.registration_hint} onChange={setValue} timeZone={view.timezone} tzLabel={view.timezone_label} value={value} />
      {error ? <Note role="alert" tone="red">{error}</Note> : null}
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <DenseButton onClick={onDone}>Cancel</DenseButton>
        <DenseButton
          disabled={pending || !value.closeAt}
          onClick={async () => {
            const ok = await run([
              { command: "set_registration_window", opens: value.opens, open_at: value.openAt, close_at: value.closeAt } as never,
            ]);
            if (ok) onDone();
          }}
          variant="primary"
        >
          Save
        </DenseButton>
      </Box>
    </Box>
  );
}

/** Edits a checklist item in place: setup sections, or the existing tab for
 * items the setup page doesn't own yet. */
export function SetupSideSheet({
  target,
  view,
  facilityId,
  onClose,
  onSaved,
}: {
  target: SheetTarget | null;
  view: TournamentSetupView;
  facilityId: string;
  onClose: () => void;
  onSaved: (next: TournamentSetupView) => void;
}) {
  const [shown, setShown] = useState<SheetTarget | null>(target);
  useEffect(() => {
    if (target) setShown(target);
  }, [target]);
  const tid = view.tournament_id;
  let body: ReactNode = null;
  if (shown === "basics") body = <BasicsForm facilityId={facilityId} onDone={onClose} onSaved={onSaved} view={view} />;
  if (shown === "registration") body = <RegistrationForm onDone={onClose} onSaved={onSaved} view={view} />;
  if (shown === "waiver") body = <LegalTab tournamentId={tid} />;
  if (shown === "officials")
    body = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <StaffTab tournamentId={tid} />
        <CoachesTab tournamentId={tid} />
      </Box>
    );
  if (shown === "promo") body = <SponsorsTab tournamentId={tid} />;
  return (
    <Drawer anchor="right" onClose={onClose} open={Boolean(target)} slotProps={{ paper: { sx: { width: { xs: "100%", sm: 620 } } } }}>
      <SetupSurface sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ height: 48, display: "flex", alignItems: "center", px: 2, borderBottom: `1px solid ${tone.divider}`, flex: "none" }}>
          <Box component="b" sx={{ fontSize: 15 }}>
            {shown ? TITLES[shown] : ""}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <DenseButton aria-label="Close" onClick={onClose} size="sm">
            <CloseIcon />
          </DenseButton>
        </Box>
        <Box sx={{ p: 2, overflowY: "auto", flexGrow: 1 }}>
          <Suspense fallback={<CircularProgress size={20} />}>{body}</Suspense>
        </Box>
      </SetupSurface>
    </Drawer>
  );
}
